/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useRef, type RefObject } from "react";
import { mergeProps, useDateSegment, useFocusRing } from "react-aria";
import type { DateFieldState, DateSegment } from "react-stately";
import { useUiColors } from "../theme";
import { useTypo } from "../typo";
import { useUiOptions } from "../ui-options-context";

export const DateTimeInputSegment = ({
  segment,
  segmentRef,
  state,
}: {
  segment: DateSegment;
  segmentRef?: RefObject<HTMLDivElement>;
  state: DateFieldState;
}) => {
  const uiColors = useUiColors();
  const typo = useTypo();
  const uiOptions = useUiOptions();

  const enableShouldForceLeadingZerosInDateField =
    uiOptions.experimental?.enableShouldForceLeadingZerosInDateField;

  const internalRef = useRef<HTMLDivElement>(null);
  const ref = segmentRef ? segmentRef : internalRef;

  const { segmentProps } = useDateSegment(segment, state, ref);
  const { isFocused, focusProps } = useFocusRing({});

  if (segment.type === "literal") {
    <span {...segmentProps}>{segment.text}</span>;
  }

  let text = segment.text;

  if (segment.type === "year" && enableShouldForceLeadingZerosInDateField) {
    const numericYear = Number(segment.text);
    if (!isNaN(numericYear)) {
      text = numericYear.toString().padStart(4, "0");
    }
  }

  return (
    <span
      {...mergeProps(segmentProps, focusProps)}
      ref={ref}
      css={{
        fontVariantNumeric: "tabular-nums",
        textAlign: "end",
        backgroundColor: isFocused ? uiColors.controlMinor : undefined,
        outlineStyle: "none",
        color: segment.isPlaceholder ? uiColors.textMinor : uiColors.text,
        ...typo({
          level: "body2",
          density: "tight",
          weight: "regular",
        }),
      }}
    >
      {text}
    </span>
  );
};
